import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Grid,
    makeStyles,
    TextField,
    Typography,
} from "@material-ui/core";
import {
    ToggleButton,
    ToggleButtonGroup
} from "@material-ui/lab";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {CKEditor} from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../util/ApiHelper";
import {toast} from "react-toastify";
import editorConfiguration
    from "../../../util/EditorConfiguration";
import {useLoading} from "../../../util/LoadingProvider";
import {useHistory} from "react-router-dom";

const schema = yup.object().shape({
    titleEn: yup.string().required("Title (En) is required."),
    titleTc: yup.string().required("Title (Tc) is required."),
});

const defaultFormValues = {
    id: 0,
    titleEn: "",
    contentEn: "",
    titleTc: "",
    contentTc: "",
    urlExtName: "",
    status: "ACTIVE",
};

const useStyles = makeStyles((theme) => ({
    textInput: {
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "362px",
        },
    },
    buttonGroup: {
        marginTop: "40px",
        textAlign: "left",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
    },
    input: {
        width: "100%",
    },
}));

export default function TicketDetailPage({
                                             disabled,
                                             ...props
                                         }) {
    const {match} = props;
    const history = useHistory();
    const id = match.params.id;
    const [defaultValues, setDefaultValues] = useState(defaultFormValues);
    const {setLoading} = useLoading();

    const classes = useStyles();

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        reset,
    } = useForm({
        defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
        resolver: yupResolver(schema),
    });

    const onSubmit = (value) => {
        if (id !== "new") updateTicket(value);
        else createTicket(value);
    };

    const getTicket = React.useCallback(async () => {
        setLoading(true);
        try {
            const result = await apiHelper.hkiff.getTicketingById(id);
            console.log(result);
            setDefaultValues(result.data);
            reset(result.data);
        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    }, [id, setLoading]);

    const updateTicket = async (value) => {
        try {
            const result = await apiHelper.hkiff.updateTicketing(id, value);
            console.log(result);
            if (result.data.id !== null || result.data.id !== undefined) {
                toast.dark("Your changes are saved.");
                getTicket();
            }
        } catch (err) {
            console.log(err);
        }
    };

    const createTicket = async (value) => {
        try {
            const result = await apiHelper.hkiff.createTicketing(value);
            console.log(result);
            if (result.data.id !== null || result.data.id !== undefined) {
                history.push("/hkiff/ticket-and-faq/ticketing");
                toast.dark("The ticket is created.");
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (id !== "new") getTicket();
    }, [getTicket, id]);

    return (
        <div className={classes.content}>
            <Box width="100%">
                <Typography variant="h4" gutterBottom>
                    Ticket &
                    FAQ &gt; {id === "new" ? "Add" : "Edit"} Ticketing
                    Info
                </Typography>
            </Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={disabled}
                          className="fieldset">
                    <Grid container xs={12} direction="row"
                          spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                name="titleEn"
                                control={control}
                                render={({
                                             field,
                                             fieldState: {error}
                                         }) => (
                                    <TextField
                                        label="Info Title (English)"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        fullWidth
                                        {...field}
                                        error={!!error}
                                        helperText={error ? error.message : "The name is how it appears on your site."}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="titleTc"
                                control={control}
                                render={({
                                             field,
                                             fieldState: {error}
                                         }) => (
                                    <TextField
                                        label="Info Title (Traditional Chinese)"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        fullWidth
                                        {...field}
                                        error={!!error}
                                        helperText={error ? error.message : "The name is how it appears on your site."}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Box
                        fontWeight={900}
                        fontSize={13}
                        mb={2}
                        style={{marginTop: "30px"}}
                        width="100%"
                    >
                        <Typography variant="p">Content
                            (English)</Typography>
                    </Box>

                    <Grid container xs={12}
                          direction="row"
                          spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                name="contentEn"
                                control={control}
                                render={({
                                             field,
                                             fieldState: {error}
                                         }) => (
                                    <CKEditor
                                        editor={Editor}
                                        config={editorConfiguration}
                                        data={field.value}
                                        disabled={disabled}
                                        onReady={(editor) => {
                                            // You can store the "editor" and use when it is needed.
                                            console.log("Editor is ready to use!", editor);
                                            editor.setData(
                                                defaultValues.contentEn
                                            );
                                        }}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            field.onChange(data);
                                            // console.log({ event, editor, data });
                                        }}
                                        onBlur={(event, editor) => {
                                            console.log("Blur.", editor);
                                        }}
                                        onFocus={(event, editor) => {
                                            console.log("Focus.", editor);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Box fontWeight={900}
                         fontSize={13} mt={4} mb={2}
                         width="100%">
                        <Typography variant="p">
                            Content (Traditional
                            Chinese)
                        </Typography>
                    </Box>

                    <Grid container xs={12}
                          direction="row"
                          spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                name="contentTc"
                                control={control}
                                render={({
                                             field,
                                             fieldState: {error}
                                         }) => (
                                    <CKEditor
                                        editor={Editor}
                                        config={editorConfiguration}
                                        data={field.value}
                                        disabled={disabled}
                                        onReady={(editor) => {
                                            // You can store the "editor" and use when it is needed.
                                            console.log("Editor is ready to use!", editor);
                                            editor.setData(
                                                defaultValues.contentTc
                                            );
                                        }}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            field.onChange(data);
                                            // console.log({ event, editor, data });
                                        }}
                                        onBlur={(event, editor) => {
                                            console.log("Blur.", editor);
                                        }}
                                        onFocus={(event, editor) => {
                                            console.log("Focus.", editor);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Grid container xs={12} direction="row"
                          style={{marginTop: '26px'}}
                          spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                name="urlExtName"
                                control={control}
                                render={({
                                             field,
                                             fieldState: {error}
                                         }) => (
                                    <TextField
                                        label="URL Ext Name"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        fullWidth
                                        {...field}
                                        error={!!error}
                                        helperText={error ? error.message : ""}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        item
                        xs={12}
                        spacing={3}
                        style={{
                            marginTop: "20px",
                            marginLeft: 0
                        }}
                    >
                        <Grid item xs={3}
                              style={{paddingLeft: "4px"}}>
                            <Controller
                                control={control}
                                name="status"
                                render={({
                                             field: {
                                                 value,
                                                 onChange,
                                                 ...rest
                                             },
                                             fieldState: {error},
                                         }) => (
                                    <ToggleButtonGroup
                                        {...rest}
                                        value={value}
                                        onChange={(event, newFormats) => {
                                            onChange(newFormats);
                                        }}
                                        exclusive
                                        aria-label="Status"
                                        className={classes.input}
                                    >
                                        <ToggleButton
                                            value={"ACTIVE"}
                                            aria-label="Active"
                                            style={{width: "50%"}}
                                            disabled={disabled}
                                        >
                                            Active
                                        </ToggleButton>
                                        <ToggleButton
                                            value={"INACTIVE"}
                                            aria-label="Inactive"
                                            style={{width: "50%"}}
                                            disabled={disabled}
                                            // disabled={true} // hardcode now as inactive leads to server error
                                        >
                                            Inactive
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                            />
                        </Grid>
                    </Grid>

                    {disabled || (
                        <div
                            className={classes.buttonGroup}>
                            <Button
                                type="submit"
                                variant="contained"
                                disableElevation
                                style={{
                                    color: "white",
                                    background: "#306F9D",
                                    marginRight: "5px",
                                    padding: "9px 16px",
                                }}
                            >
                                Save Changes
                            </Button>
                        </div>
                    )}
                </fieldset>
            </form>
        </div>
    );
}
